type ExcelCellValue = string | number | boolean | Date | null | undefined;

export type ExcelReportColumn<TRow> = {
    header: string;
    width?: number;
    value: (row: TRow) => ExcelCellValue;
    alignment?: "left" | "center" | "right";
    wrap?: boolean;
    numFmt?: string;
};

export type ExcelReportMetric = {
    label: string;
    value: string | number;
    note?: string;
};

export type ExcelReportSection = {
    title: string;
    rows: Array<{
        label: string;
        value: string | number;
        note?: string;
    }>;
};

export type Ride2FiveExcelReportOptions<TRow> = {
    title: string;
    subtitle: string;
    fileName: string;
    rows: TRow[];
    columns: ExcelReportColumn<TRow>[];
    filters?: Record<string, unknown>;
    metrics?: ExcelReportMetric[];
    sections?: ExcelReportSection[];
    dataSheetName?: string;
};

export type Ride2FiveExcelWorkbookSheet<TRow = any> = {
    sheetName: string;
    title?: string;
    rows: TRow[];
    columns: ExcelReportColumn<TRow>[];
};

export type Ride2FiveExcelWorkbookOptions = {
    title: string;
    subtitle: string;
    fileName: string;
    filters?: Record<string, unknown>;
    metrics?: ExcelReportMetric[];
    sections?: ExcelReportSection[];
    sheets: Ride2FiveExcelWorkbookSheet<any>[];
};

const BRAND_ORANGE = "F04B23";
const BRAND_ORANGE_DARK = "D93C15";
const BRAND_CREAM = "FFF3EE";
const BRAND_TEXT = "1E293B";
const MUTED_TEXT = "64748B";
const BORDER = "E2E8F0";
const HEADER_FILL = "F8FAFC";
const WHITE = "FFFFFF";

const formatDisplayValue = (value: unknown) => {
    if (value == null || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value instanceof Date) return value.toLocaleString("en-NG");
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
};

const humanizeKey = (value: string) =>
    value
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const slugify = (value: string) =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const buildTimestamp = () => {
    const now = new Date();
    const datePart = now.toISOString().slice(0, 10);
    const timePart = now
        .toTimeString()
        .slice(0, 8)
        .replace(/:/g, "-");

    return `${datePart}_${timePart}`;
};

const autoFitWidth = (values: string[], fallback = 18) =>
    Math.max(
        fallback,
        Math.min(
            40,
            values.reduce((max, value) => Math.max(max, value.length + 2), 0)
        )
    );

const normalizeFileName = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.toLowerCase().endsWith(".xlsx")) return trimmed;
    return `${trimmed}.xlsx`;
};

const addSectionHeader = (
    worksheet: import("exceljs").Worksheet,
    rowIndex: number,
    title: string,
    totalColumns: number
) => {
    worksheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
    const cell = worksheet.getCell(rowIndex, 1);
    cell.value = title;
    cell.font = {
        bold: true,
        color: { argb: BRAND_ORANGE_DARK },
        size: 12,
    };
    cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: BRAND_CREAM },
    };
    cell.border = {
        top: { style: "thin", color: { argb: BORDER } },
        left: { style: "thin", color: { argb: BORDER } },
        bottom: { style: "thin", color: { argb: BORDER } },
        right: { style: "thin", color: { argb: BORDER } },
    };
};

const buildWorkbookOverviewSheet = ({
    workbook,
    title,
    subtitle,
    fileName,
    filters = {},
    metrics = [],
    sections = [],
    totalExportedRows,
    generatedAt,
}: {
    workbook: import("exceljs").Workbook;
    title: string;
    subtitle: string;
    fileName: string;
    filters?: Record<string, unknown>;
    metrics?: ExcelReportMetric[];
    sections?: ExcelReportSection[];
    totalExportedRows: number;
    generatedAt: Date;
}) => {
    const overviewSheet = workbook.addWorksheet("Overview", {
        views: [{ state: "frozen", ySplit: 4 }],
    });

    const overviewWidth = 6;
    overviewSheet.columns = new Array(overviewWidth).fill(null).map(() => ({ width: 24 }));
    overviewSheet.mergeCells(1, 1, 2, overviewWidth);
    const titleCell = overviewSheet.getCell(1, 1);
    titleCell.value = `Ride2Five ${title}`;
    titleCell.font = { size: 20, bold: true, color: { argb: WHITE } };
    titleCell.alignment = { vertical: "middle", horizontal: "left" };
    titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: BRAND_ORANGE },
    };

    overviewSheet.mergeCells(3, 1, 3, overviewWidth);
    const subtitleCell = overviewSheet.getCell(3, 1);
    subtitleCell.value = subtitle;
    subtitleCell.font = { size: 11, color: { argb: MUTED_TEXT } };

    let overviewRow = 5;
    addSectionHeader(overviewSheet, overviewRow, "Report Snapshot", overviewWidth);
    overviewRow += 1;

    [
        ["Generated At", generatedAt.toLocaleString("en-NG")],
        ["Generated By", "Ride2Five Admin Portal"],
        ["Total Exported Rows", totalExportedRows.toLocaleString("en-NG")],
        ["Workbook", fileName],
    ].forEach(([label, value]) => {
        overviewSheet.getCell(overviewRow, 1).value = label;
        overviewSheet.getCell(overviewRow, 1).font = { bold: true, color: { argb: BRAND_TEXT } };
        overviewSheet.mergeCells(overviewRow, 2, overviewRow, overviewWidth);
        overviewSheet.getCell(overviewRow, 2).value = value;
        overviewRow += 1;
    });

    const appliedFilters = getAppliedFilterEntries(filters);
    overviewRow += 1;
    addSectionHeader(overviewSheet, overviewRow, "Applied Filters", overviewWidth);
    overviewRow += 1;

    if (!appliedFilters.length) {
        overviewSheet.mergeCells(overviewRow, 1, overviewRow, overviewWidth);
        overviewSheet.getCell(overviewRow, 1).value = "No active filters were applied.";
        overviewSheet.getCell(overviewRow, 1).font = { italic: true, color: { argb: MUTED_TEXT } };
        overviewRow += 1;
    } else {
        appliedFilters.forEach(({ label, value }) => {
            overviewSheet.getCell(overviewRow, 1).value = label;
            overviewSheet.getCell(overviewRow, 1).font = { bold: true, color: { argb: BRAND_TEXT } };
            overviewSheet.mergeCells(overviewRow, 2, overviewRow, overviewWidth);
            overviewSheet.getCell(overviewRow, 2).value = value;
            overviewRow += 1;
        });
    }

    if (metrics.length) {
        overviewRow += 1;
        addSectionHeader(overviewSheet, overviewRow, "Key Metrics", overviewWidth);
        overviewRow += 1;

        metrics.forEach((metric) => {
            overviewSheet.getCell(overviewRow, 1).value = metric.label;
            overviewSheet.getCell(overviewRow, 1).font = {
                bold: true,
                color: { argb: BRAND_TEXT },
            };
            overviewSheet.getCell(overviewRow, 2).value = metric.value;
            overviewSheet.getCell(overviewRow, 2).font = {
                bold: true,
                color: { argb: BRAND_ORANGE_DARK },
            };

            if (metric.note) {
                overviewSheet.mergeCells(overviewRow, 3, overviewRow, overviewWidth);
                overviewSheet.getCell(overviewRow, 3).value = metric.note;
                overviewSheet.getCell(overviewRow, 3).font = {
                    italic: true,
                    color: { argb: MUTED_TEXT },
                };
            }

            overviewRow += 1;
        });
    }

    sections.forEach((section) => {
        if (!section.rows.length) return;

        overviewRow += 1;
        addSectionHeader(overviewSheet, overviewRow, section.title, overviewWidth);
        overviewRow += 1;

        section.rows.forEach((row) => {
            overviewSheet.getCell(overviewRow, 1).value = row.label;
            overviewSheet.getCell(overviewRow, 1).font = { color: { argb: BRAND_TEXT } };
            overviewSheet.getCell(overviewRow, 2).value = row.value;
            overviewSheet.getCell(overviewRow, 2).font = {
                bold: true,
                color: { argb: BRAND_TEXT },
            };

            if (row.note) {
                overviewSheet.mergeCells(overviewRow, 3, overviewRow, overviewWidth);
                overviewSheet.getCell(overviewRow, 3).value = row.note;
                overviewSheet.getCell(overviewRow, 3).font = {
                    italic: true,
                    color: { argb: MUTED_TEXT },
                };
            }

            overviewRow += 1;
        });
    });
};

const addWorkbookDataSheet = <TRow>({
    workbook,
    title,
    sheetName,
    rows,
    columns,
}: {
    workbook: import("exceljs").Workbook;
    title: string;
    sheetName: string;
    rows: TRow[];
    columns: ExcelReportColumn<TRow>[];
}) => {
    const dataSheet = workbook.addWorksheet(sheetName, {
        views: [{ state: "frozen", ySplit: 2 }],
    });
    dataSheet.properties.defaultRowHeight = 22;
    dataSheet.columns = columns.map((column) => ({
        width: column.width ?? autoFitWidth([column.header], 18),
    }));

    dataSheet.mergeCells(1, 1, 1, columns.length);
    const dataTitleCell = dataSheet.getCell(1, 1);
    dataTitleCell.value = `Ride2Five ${title}`;
    dataTitleCell.font = { size: 16, bold: true, color: { argb: WHITE } };
    dataTitleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: BRAND_ORANGE },
    };
    dataTitleCell.alignment = { vertical: "middle", horizontal: "left" };

    const headerRowIndex = 2;
    const headerRow = dataSheet.getRow(headerRowIndex);
    columns.forEach((column, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = column.header;
        cell.font = { bold: true, color: { argb: BRAND_TEXT } };
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: HEADER_FILL },
        };
        cell.border = {
            top: { style: "thin", color: { argb: BORDER } },
            left: { style: "thin", color: { argb: BORDER } },
            bottom: { style: "thin", color: { argb: BORDER } },
            right: { style: "thin", color: { argb: BORDER } },
        };
    });

    rows.forEach((row, rowIndex) => {
        const worksheetRow = dataSheet.getRow(headerRowIndex + rowIndex + 1);
        columns.forEach((column, columnIndex) => {
            const cell = worksheetRow.getCell(columnIndex + 1);
            const value = column.value(row);
            cell.value = value == null ? "—" : value;
            cell.alignment = {
                vertical: "top",
                horizontal: column.alignment ?? "left",
                wrapText: column.wrap ?? true,
            };

            if (column.numFmt) {
                cell.numFmt = column.numFmt;
            }

            cell.border = {
                top: { style: "thin", color: { argb: BORDER } },
                left: { style: "thin", color: { argb: BORDER } },
                bottom: { style: "thin", color: { argb: BORDER } },
                right: { style: "thin", color: { argb: BORDER } },
            };

            if (rowIndex % 2 === 1) {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FCFCFD" },
                };
            }
        });
    });

    dataSheet.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: columns.length },
    };

    columns.forEach((column, index) => {
        const previewValues = rows
            .slice(0, 50)
            .map((row) => formatDisplayValue(column.value(row)));
        dataSheet.getColumn(index + 1).width =
            column.width ?? autoFitWidth([column.header, ...previewValues], 18);
    });
};

const downloadWorkbook = async (
    workbook: import("exceljs").Workbook,
    normalizedFileName: string
) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = normalizedFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const createExportFileName = (prefix: string) =>
    `ride2five_${slugify(prefix)}_${buildTimestamp()}.xlsx`;

export const countBy = <TRow>(
    rows: TRow[],
    getValue: (row: TRow) => string | null | undefined
) => {
    return rows.reduce<Record<string, number>>((accumulator, row) => {
        const rawValue = getValue(row);
        const key = rawValue && rawValue.trim().length > 0 ? rawValue : "UNSPECIFIED";
        accumulator[key] = (accumulator[key] ?? 0) + 1;
        return accumulator;
    }, {});
};

export const sumBy = <TRow>(rows: TRow[], getValue: (row: TRow) => number | null | undefined) =>
    rows.reduce((total, row) => total + (getValue(row) ?? 0), 0);

export const averageBy = <TRow>(
    rows: TRow[],
    getValue: (row: TRow) => number | null | undefined
) => {
    const values = rows
        .map((row) => getValue(row))
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

    if (!values.length) return null;

    return values.reduce((total, value) => total + value, 0) / values.length;
};

export const uniqueCountBy = <TRow>(
    rows: TRow[],
    getValue: (row: TRow) => string | null | undefined
) =>
    new Set(
        rows
            .map((row) => getValue(row))
            .filter((value): value is string => Boolean(value && value.trim()))
    ).size;

export const buildBreakdownRows = (
    counts: Record<string, number>,
    options: { humanizeLabels?: boolean } = {}
) =>
    Object.entries(counts)
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .map(([label, value]) => ({
            label: options.humanizeLabels === false ? label : humanizeKey(label),
            value,
        }));

export const buildCurrencyBreakdownRows = <TRow>(
    rows: TRow[],
    getCurrency: (row: TRow) => string | null | undefined,
    getAmount: (row: TRow) => number | null | undefined
) => {
    const totals = rows.reduce<Record<string, number>>((accumulator, row) => {
        const currency = getCurrency(row) ?? "UNKNOWN";
        accumulator[currency] = (accumulator[currency] ?? 0) + (getAmount(row) ?? 0);
        return accumulator;
    }, {});

    return Object.entries(totals)
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .map(([currency, total]) => ({
            label: currency,
            value: total.toLocaleString("en-NG", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }),
        }));
};

export const getAppliedFilterEntries = (filters: Record<string, unknown> = {}) =>
    Object.entries(filters)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => ({
            label: humanizeKey(key),
            value: formatDisplayValue(value),
        }));

export const collectPaginatedRows = async <TPage, TRow>({
    fetchPage,
    getRows,
    getTotalPages,
}: {
    fetchPage: (page: number) => Promise<TPage>;
    getRows: (pageData: TPage) => TRow[];
    getTotalPages: (pageData: TPage) => number;
}) => {
    const firstPage = await fetchPage(0);
    const rows = [...getRows(firstPage)];
    const totalPages = Math.max(getTotalPages(firstPage), 1);

    for (let page = 1; page < totalPages; page += 1) {
        const pageData = await fetchPage(page);
        rows.push(...getRows(pageData));
    }

    return rows;
};

export const exportRide2FiveExcelReport = async <TRow>({
    title,
    subtitle,
    fileName,
    rows,
    columns,
    filters = {},
    metrics = [],
    sections = [],
    dataSheetName = "Data",
}: Ride2FiveExcelReportOptions<TRow>) => {
    return exportRide2FiveExcelWorkbook({
        title,
        subtitle,
        fileName,
        filters,
        metrics,
        sections,
        sheets: [
            {
                sheetName: dataSheetName,
                title,
                rows,
                columns,
            },
        ],
    });
};

export const exportRide2FiveExcelWorkbook = async ({
    title,
    subtitle,
    fileName,
    filters = {},
    metrics = [],
    sections = [],
    sheets,
}: Ride2FiveExcelWorkbookOptions) => {
    const ExcelJs = await import("exceljs");
    const workbook = new ExcelJs.Workbook();
    const normalizedFileName = normalizeFileName(fileName);
    const generatedAt = new Date();

    workbook.creator = "Ride2Five Admin Portal";
    workbook.company = "Ride2Five";
    workbook.created = generatedAt;
    workbook.modified = generatedAt;
    workbook.title = title;
    workbook.subject = subtitle;

    buildWorkbookOverviewSheet({
        workbook,
        title,
        subtitle,
        fileName: normalizedFileName,
        filters,
        metrics,
        sections,
        totalExportedRows: sheets.reduce((total, sheet) => total + sheet.rows.length, 0),
        generatedAt,
    });

    sheets.forEach((sheet) => {
        addWorkbookDataSheet({
            workbook,
            title: sheet.title ?? title,
            sheetName: sheet.sheetName,
            rows: sheet.rows,
            columns: sheet.columns,
        });
    });

    await downloadWorkbook(workbook, normalizedFileName);
};
